/**
 * WHAT: Attester audit report tracking attester replacement history and attestation activity per campaign.
 * WHY:  Regulatory audits require evidence of who performed attestations and whether attesters were
 *       substituted during a campaign; this report provides that chain-of-custody trail to demonstrate
 *       accountability and proper delegation.
 */
import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api.config';
import { formatDateTime } from '../utils/dateUtils';

/**
 * WHAT: Renders attester replacement and activity audit data for a selected campaign with search and CSV export.
 * WHY:  Allows compliance teams to review which attesters were replaced, who replaced them, and the
 *       detailed attestation actions each attester took, supporting SOX and regulatory audit requirements.
 */
const AttesterAuditReport = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('replacements');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setIsLoadingCampaigns(true);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/'));
      const data = await response.json();

      if (data.success) {
        // Filter campaigns that have been submitted for attestation (not DRAFT)
        const eligibleCampaigns = data.campaigns.filter(
          campaign => campaign.status !== 'DRAFT'
        );
        setCampaigns(eligibleCampaigns);
      } else {
        setError('Failed to load campaigns');
      }
    } catch (err) {
      setError('Error loading campaigns: ' + err.message);
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const loadReport = async () => {
    if (!selectedCampaignId) {
      setError('Please select a campaign');
      return;
    }

    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`reports/attester-audit/?campaign_id=${selectedCampaignId}`)
      );
      const data = await response.json();

      if (data.success) {
        setReportData(data);
      } else {
        setError(data.error || 'Failed to load report');
      }
    } catch (err) {
      setError('Error loading report: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = async () => {
    if (!selectedCampaignId) {
      setError('Please select a campaign first');
      return;
    }

    try {
      window.open(
        API_CONFIG.getDatabasesEndpoint(`reports/attester-audit/export/?campaign_id=${selectedCampaignId}`),
        '_blank'
      );
    } catch (err) {
      setError('Error exporting CSV: ' + err.message);
    }
  };

  // Use formatDateTime from dateUtils for consistent formatting with timezone

  const getReasonBadgeClass = (reasonCode) => {
    switch (reasonCode) {
      case 'OUT_OF_OFFICE': return 'bg-warning text-dark';
      case 'TEAM_CHANGE': return 'bg-info';
      case 'TERMINATION': return 'bg-danger';
      case 'ACCOUNT_OWNER': return 'bg-primary';
      default: return 'bg-secondary';
    }
  };

  const filterData = (data) => {
    if (!searchTerm || !data) return data;
    const search = searchTerm.toLowerCase();
    return data.filter(item =>
      JSON.stringify(item).toLowerCase().includes(search)
    );
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <h2 className="text-primary mb-4">
            <i className="fas fa-user-shield me-2"></i>
            Attester Audit Report
          </h2>

          <div className="card shadow-sm mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-filter me-2"></i>
                Report Selection
              </h5>
            </div>
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Select Campaign</label>
                  {isLoadingCampaigns ? (
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <select
                      className="form-select"
                      value={selectedCampaignId}
                      onChange={(e) => setSelectedCampaignId(e.target.value)}
                    >
                      <option value="">-- Select a Campaign --</option>
                      {campaigns.map(campaign => (
                        <option key={campaign.campaign_id} value={campaign.campaign_id}>
                          {campaign.campaign_name} ({campaign.compliance_framework}) - {campaign.status}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-primary w-100"
                    onClick={loadReport}
                    disabled={isLoading || !selectedCampaignId}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Loading
                      </>
                    ) : (
                      <>
                        <i className="fas fa-search me-1"></i>
                        Generate
                      </>
                    )}
                  </button>
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-success w-100"
                    onClick={exportToCSV}
                    disabled={!selectedCampaignId || !reportData}
                  >
                    <i className="fas fa-file-csv me-1"></i>
                    Export CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show">
              <i className="fas fa-exclamation-circle me-2"></i>
              {error}
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          )}

          {reportData && (
            <>
              {/* Executive Summary */}
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-info text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-clipboard-list me-2"></i>
                    Executive Summary
                  </h5>
                </div>
                <div className="card-body">
                  {/* Campaign Details & Statistics - Split Screen */}
                  <div className="row">
                    {/* Campaign Details */}
                    <div className="col-md-6 border-end">
                      <h6 className="text-info mb-2">
                        <i className="fas fa-info-circle me-2"></i>
                        Campaign Details
                      </h6>
                      <div className="row">
                        <div className="col-6">
                          <p className="mb-1"><strong>Campaign Name:</strong> {reportData.executive_summary.campaign_name}</p>
                          <p className="mb-1"><strong>Compliance Framework:</strong> {reportData.executive_summary.compliance_framework || 'N/A'}</p>
                          <p className="mb-1"><strong>Database Type:</strong> {reportData.executive_summary.database_type || 'N/A'}</p>
                        </div>
                        <div className="col-6">
                          <p className="mb-1"><strong>Start Date:</strong> {formatDateTime(reportData.executive_summary.start_date)}</p>
                          <p className="mb-1"><strong>End Date:</strong> {formatDateTime(reportData.executive_summary.end_date)}</p>
                          <p className="mb-1"><strong>Report Generated:</strong> {formatDateTime(reportData.executive_summary.report_generated_at)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Audit Statistics */}
                    <div className="col-md-6">
                      <h6 className="text-info mb-2">
                        <i className="fas fa-chart-bar me-2"></i>
                        Audit Summary
                      </h6>
                      <p className="mb-1"><strong>Attester Replacements:</strong> <span className="badge bg-primary">{reportData.attester_replacements.length}</span></p>
                      <p className="mb-1"><strong>Attester Delegations:</strong> <span className="badge bg-info">{reportData.attestation_delegations.length}</span></p>
                    </div>
                  </div>

                  {/* Stats Summary */}
                  <div className="mt-3 pt-3 border-top">
                    <div className="row">
                      <div className="col-md-6">
                        <div
                          className="d-flex align-items-center p-2 border rounded"
                          style={{cursor: 'pointer', backgroundColor: '#f8f9fa'}}
                          onClick={() => setActiveTab('replacements')}
                          title="Click to view Replacements"
                        >
                          <i className="fas fa-user-edit text-primary me-3 fa-lg"></i>
                          <div>
                            <strong>Attester Replacements</strong>
                            <small className="text-muted d-block">by Campaign Admin</small>
                          </div>
                          <span className="badge bg-primary ms-auto">{reportData.attester_replacements.length}</span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div
                          className="d-flex align-items-center p-2 border rounded"
                          style={{cursor: 'pointer', backgroundColor: '#f8f9fa'}}
                          onClick={() => setActiveTab('delegations')}
                          title="Click to view Delegations"
                        >
                          <i className="fas fa-share text-info me-3 fa-lg"></i>
                          <div>
                            <strong>Attester Delegations</strong>
                            <small className="text-muted d-block">by Original Attester</small>
                          </div>
                          <span className="badge bg-info ms-auto">{reportData.attestation_delegations.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs for different sections */}
              <div className="card shadow-sm">
                <div className="card-header bg-dark text-white">
                  <ul className="nav nav-tabs card-header-tabs">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === 'replacements' ? 'active bg-white text-dark' : 'text-white'}`}
                        onClick={() => setActiveTab('replacements')}
                      >
                        <i className="fas fa-user-edit me-1"></i>
                        Replacements - by Admin ({reportData.attester_replacements.length})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === 'delegations' ? 'active bg-white text-dark' : 'text-white'}`}
                        onClick={() => setActiveTab('delegations')}
                      >
                        <i className="fas fa-share me-1"></i>
                        Delegations - by Attester ({reportData.attestation_delegations.length})
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="card-body">
                  {/* Search */}
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Replacements Tab */}
                  {activeTab === 'replacements' && (
                    <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {filterData(reportData.attester_replacements).length === 0 ? (
                        <div className="alert alert-info">No attester replacements found for this campaign.</div>
                      ) : (
                        <table className="table table-sm table-bordered table-striped table-hover">
                          <thead className="table-dark sticky-top">
                            <tr>
                              <th>Database</th>
                              <th>Username</th>
                              <th>Original Attester</th>
                              <th>Replaced With</th>
                              <th>Reason</th>
                              <th>Replaced By</th>
                              <th>Replacement Date</th>
                              <th>Comments</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filterData(reportData.attester_replacements).map((item, index) => (
                              <tr key={item.replacement_id || index}>
                                <td><strong>{item.database_name}</strong></td>
                                <td><code>{item.db_username}</code></td>
                                <td>
                                  <div><small className="text-muted">{item.old_attester_email}</small></div>
                                  {item.old_attester_employee_id && (
                                    <div><small className="text-info">{item.old_attester_employee_id}</small></div>
                                  )}
                                </td>
                                <td>
                                  <div><strong>{item.new_attester_email}</strong></div>
                                  {item.new_attester_employee_id && (
                                    <div><small className="text-info">{item.new_attester_employee_id}</small></div>
                                  )}
                                </td>
                                <td>
                                  {item.reason ? (
                                    <span className={`badge ${getReasonBadgeClass(item.reason)}`}>
                                      {item.reason}
                                    </span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  <div><strong>{item.replaced_by || 'N/A'}</strong></div>
                                  {item.replaced_by_employee_id && (
                                    <div><small className="text-info">{item.replaced_by_employee_id}</small></div>
                                  )}
                                </td>
                                <td>
                                  <small>{formatDateTime(item.replacement_date)}</small>
                                </td>
                                <td>
                                  <small>{item.comments || '-'}</small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* Delegations Tab */}
                  {activeTab === 'delegations' && (
                    <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {filterData(reportData.attestation_delegations).length === 0 ? (
                        <div className="alert alert-info">No attestation delegations found for this campaign.</div>
                      ) : (
                        <table className="table table-sm table-bordered table-striped table-hover">
                          <thead className="table-dark sticky-top">
                            <tr>
                              <th>Database</th>
                              <th>Username</th>
                              <th>Original Attester</th>
                              <th>Delegated To</th>
                              <th>Reason</th>
                              <th>Delegated By</th>
                              <th>Delegation Date</th>
                              <th>Comments</th>
                              <th>IP Address</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filterData(reportData.attestation_delegations).map((item, index) => (
                              <tr key={item.delegation_id || index}>
                                <td><strong>{item.database_name}</strong></td>
                                <td><code>{item.db_username}</code></td>
                                <td>
                                  <div><small className="text-muted">{item.from_attester_email}</small></div>
                                  {item.from_attester_employee_id && (
                                    <div><small className="text-info">{item.from_attester_employee_id}</small></div>
                                  )}
                                </td>
                                <td>
                                  <div><strong>{item.to_attester_email}</strong></div>
                                  {item.to_attester_employee_id && (
                                    <div><small className="text-info">{item.to_attester_employee_id}</small></div>
                                  )}
                                </td>
                                <td>
                                  {item.delegation_reason ? (
                                    <span className="badge bg-info">{item.delegation_reason}</span>
                                  ) : (
                                    <span className="text-muted">N/A</span>
                                  )}
                                </td>
                                <td>
                                  <div><strong>{item.delegated_by || 'N/A'}</strong></div>
                                  {item.delegated_by_employee_id && (
                                    <div><small className="text-info">{item.delegated_by_employee_id}</small></div>
                                  )}
                                </td>
                                <td>
                                  <small>{formatDateTime(item.delegation_date)}</small>
                                </td>
                                <td>
                                  <small>{item.comments || '-'}</small>
                                </td>
                                <td>
                                  <small className="text-muted">{item.delegation_ip_address || 'N/A'}</small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                </div>
              </div>
            </>
          )}

          {!reportData && !isLoading && !error && (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              Select a campaign and click "Generate Report" to view attester changes:
              <ul className="mb-0 mt-2">
                <li><strong>Replacements</strong> - Attester changes made by the Campaign Admin</li>
                <li><strong>Delegations</strong> - Attestations delegated by the original assigned attester</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttesterAuditReport;
